import { useMemo, useState } from "react";

/**
 * Poenostavljen kalkulator za primerjavo:
 * - Osebno dopolnilno delo (ODD)
 * - Popoldanski s.p. (normirani)
 *
 * Predpostavke (poenostavljeno, ne šteje kot davčni nasvet):
 * - ODD: vrednotnica ~11,17 €/mesec = 134,00 €/leto; akontacija dohodnine 25 % od 90 % osnove.
 *   Zakonska letna omejitev prihodkov iz ODD je ~4.500,00 € (nad tem ODD ni več mogoč).
 * - Popoldanski s.p. normiranec: pavšalni prispevki ~90,00 €/mesec = ~1.080,00 €/leto (okvirno).
 *   Priznani normirani odhodki 80 % → davčna osnova 20 % prihodkov.
 *   Efektivna dohodnina 20 % od osnove (poenostavljeno; cedularno).
 */

function fmt(v: number) {
  return new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

const ODD_LIMIT = 4500;
const ODD_VRED_YEAR = 134;
const SP_CONTRIB_YEAR = 1080;

export default function OddSpCalculator() {
  const [promet, setPromet] = useState<number>(12000);

  const { odd, sp } = useMemo(() => {
    // ODD
    const oddPossible = promet <= ODD_LIMIT;
    const oddBase = promet * 0.9;
    const oddTax = oddBase * 0.25;
    const oddCost = ODD_VRED_YEAR + oddTax;
    const oddNet = promet - oddCost;

    // Popoldanski s.p. normiranec
    const spBase = promet * 0.2;
    const spTax = spBase * 0.2;
    const spCost = SP_CONTRIB_YEAR + spTax;
    const spNet = promet - spCost;

    return {
      odd: { possible: oddPossible, base: oddBase, tax: oddTax, contrib: ODD_VRED_YEAR, total: oddCost, net: oddNet },
      sp:  { base: spBase, tax: spTax, contrib: SP_CONTRIB_YEAR, total: spCost, net: spNet },
    };
  }, [promet]);

  const presets = [3000, 6000, 12000, 18000, 30000];

  return (
    <section
      aria-label="Kalkulator ODD vs popoldanski s.p."
      className="my-12 rounded-xl border border-border bg-muted/30 p-6 md:p-8 not-prose"
    >
      <h3 className="font-serif text-2xl md:text-3xl">
        Kalkulator: <span className=" text-accent">ODD vs popoldanski s.p.</span>
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Vnesite predviden letni promet in primerjajte skupne stroške ter neto zaslužek. Izračun je okviren in ne nadomešča davčnega nasveta.
      </p>

      <div className="mt-6">
        <div className="flex items-baseline justify-between gap-4">
          <label htmlFor="promet" className="block text-sm font-medium">
            Letni promet
          </label>
          <span className="text-lg font-semibold text-accent" aria-live="polite">
            {fmt(promet)}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <input
            id="promet"
            type="number"
            min={0}
            step={100}
            value={promet}
            onChange={(e) => setPromet(Math.max(0, Number(e.target.value) || 0))}
            className="w-40 rounded-md border border-border bg-background px-3 py-2 text-base"
            aria-label="Letni promet v evrih"
          />
          <input
            type="range"
            min={0}
            max={40000}
            step={500}
            value={promet}
            onChange={(e) => setPromet(Number(e.target.value))}
            className="flex-1 min-w-[180px] accent-[hsl(var(--accent))]"
            aria-label="Drsnik za letni promet"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPromet(p)}
              className={`text-xs px-3 py-1.5 rounded-full border bg-transparent transition-colors ${
                promet === p
                  ? "border-accent text-accent font-semibold"
                  : "border-border hover:border-accent"
              }`}
            >
              {fmt(p)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {/* ODD */}
        <div className="rounded-lg border border-border bg-background p-5">
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-xl">Osebno dopolnilno delo</h4>
            {!odd.possible && (
              <span className="text-[0.65rem] uppercase tracking-wider text-destructive">Nad limitom</span>
            )}
          </div>
          {!odd.possible ? (
            <p className="mt-3 text-sm text-muted-foreground">
              ODD je omejen na ~{fmt(ODD_LIMIT)} letnega prometa. Pri tem prometu ni več na voljo — na voljo je popoldanski s.p.
            </p>
          ) : (
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Vrednotnice (letno)" value={fmt(odd.contrib)} />
              <Row label="Davčna osnova (90 %)" value={fmt(odd.base)} />
              <Row label="Akontacija dohodnine (25 %)" value={fmt(odd.tax)} />
              <hr className="my-3 border-border" />
              <Row label="Skupni stroški" value={fmt(odd.total)} strong />
              <Row label="Neto zaslužek" value={fmt(odd.net)} strong accent />
            </dl>
          )}
        </div>

        {/* SP */}
        <div className="rounded-lg border border-border bg-background p-5">
          <h4 className="font-serif text-xl">Popoldanski s.p. (normiranec)</h4>
          <dl className="mt-4 space-y-2 text-sm">
            <Row label="Pavšalni prispevki (letno)" value={fmt(sp.contrib)} />
            <Row label="Davčna osnova (20 %)" value={fmt(sp.base)} />
            <Row label="Dohodnina (20 %)" value={fmt(sp.tax)} />
            <hr className="my-3 border-border" />
            <Row label="Skupni stroški" value={fmt(sp.total)} strong />
            <Row label="Neto zaslužek" value={fmt(sp.net)} strong accent />
          </dl>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-accent/30 bg-accent/5 p-4 text-sm">
        <strong>Priporočilo pri {fmt(promet)}:</strong>{" "}
        {!odd.possible
          ? "Popoldanski s.p. je edina možnost — ODD je nad zakonskim limitom."
          : odd.net > sp.net
            ? "ODD je ugodnejši (nižji skupni stroški)."
            : "Popoldanski s.p. je ugodnejši (nižji skupni stroški)."}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        * Izračun je poenostavljen: uporablja pavšal ~11,17 €/mesec za vrednotnice, ~90,00 €/mesec pavšalnih prispevkov za popoldanski s.p., 25 % akontacije dohodnine (ODD) in 20 % efektivne cedularne dohodnine (s.p. normiranec). Dejanski stroški se lahko razlikujejo — preverite pri FURS/AJPES ali računovodji.
      </p>
    </section>
  );
}

function Row({
  label,
  value,
  strong,
  accent,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`${strong ? "font-semibold" : ""} ${accent ? "text-accent" : ""}`}>{value}</dd>
    </div>
  );
}
